import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { compareCzech, sortBySearchMatch } from "@ckgo2/shared";
import { TEAM_SECTION_TYPES, type TeamSectionType } from "@ckgo2/shared/types";
import { Plus, Search, Users, Edit, Trash2, MapPin } from "lucide-react";
import i18n from "@/i18n";
import { ADMIN_TEAM_LIST_KEY, useAdminTeamList } from "@/components/people/queries";
import { teamHasRole, teamRoles } from "@/components/people/teamDestinations";

/**
 * ZRCADLA sdíleného výčtu typů sekcí (viz `DestinationTeamEditPage.tsx`). Typ
 * `Record<TeamSectionType, …>` znamená, že nový typ bez popisku a bez barvy
 * neprojde `pnpm typecheck`; dřív se tiše vykreslila syrová hodnota.
 */
const SECTION_LABELS: Record<TeamSectionType, string> = {
  guarantor: i18n.t("catalog.teams.sections.guarantor"),
  sales_team: i18n.t("catalog.teams.sections.salesTeam"),
  guide: i18n.t("catalog.teams.sections.guide"),
  local_team: i18n.t("catalog.teams.sections.localTeam"),
  flight_booking: i18n.t("catalog.teams.sections.flightBooking"),
  management: i18n.t("catalog.teams.sections.management"),
  branch_manager: i18n.t("catalog.teams.sections.branchManager"),
};

const SECTION_COLORS: Record<TeamSectionType, string> = {
  guarantor: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  sales_team: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  guide: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  local_team: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  flight_booking: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  management: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  branch_manager: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
};

export default function DestinationTeamsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<TeamSectionType | "">("");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useAdminTeamList();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/destination-teams/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_TEAM_LIST_KEY });
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(t("catalog.confirmDeleteNamed", { name }))) {
      deleteMutation.mutate(id);
    }
  };

  const teams = data?.data || [];

  // Role filtruje (karta s tou rolí u KTERÉKOLI své destinace; karta bez destinací žádnou
  // roli nemá); hledání filtruje i řadí podle shody s názvem týmu, shoda jen v destinaci jde
  // za ní. Při stejné shodě a bez hledání abecedně podle jména (`compareCzech`); sloupec
  // Pořadí ukazuje `sortOrder` (pořadí první destinace), seznam podle něj neřadí.
  const filteredTeams = sortBySearchMatch(
    teams.filter((team) => !roleFilter || teamHasRole(team, roleFilter)),
    search,
    {
      name: (team) => team.name,
      other: (team) => team.destinationNames,
      tieBreak: (a, b) => compareCzech(a.name, b.name),
    },
  );

  if (error) {
    return (
      <div className="card text-error-600 text-center">{t("catalog.teams.list.loadError")}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("catalog.teams.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("catalog.teams.list.subtitle")}
          </p>
        </div>
        <Link to="/destination-teams/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("catalog.teams.list.new")}
        </Link>
      </div>

      {/* Filters */}
      <div className="card flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("catalog.teams.list.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          data-team-role-filter=""
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as TeamSectionType | "")}
          className="input w-full sm:w-48"
        >
          <option value="">{t("people.list.allRoles")}</option>
          {TEAM_SECTION_TYPES.map((role) => (
            <option key={role} value={role}>
              {SECTION_LABELS[role]}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <Users className="mb-2 h-12 w-12 text-gray-300" />
          <p>{t("catalog.teams.list.empty")}</p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t("common.fields.name")}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t("people.list.rolesColumn")}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t("common.fields.destination")}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t("common.fields.sortOrder")}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t("common.fields.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredTeams.map((team) => {
                const roles = teamRoles(team);
                return (
                  <tr
                    key={team.id}
                    data-team-row={team.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {team.photo ? (
                          <img
                            src={team.photo}
                            alt={team.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                            <Users className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{team.name}</p>
                          {team.subtitle && (
                            <p className="text-sm text-gray-500">{team.subtitle}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {/* Role karty u jejích destinací bez duplicit v kanonickém pořadí;
                          karta bez destinací žádnou roli na stránkách destinací nemá. */}
                      <span data-team-roles="" className="flex flex-wrap gap-1">
                        {roles.length === 0 ? (
                          <span className="text-sm text-gray-400">—</span>
                        ) : (
                          roles.map((role) => (
                            <span
                              key={role}
                              data-role={role}
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${SECTION_COLORS[role]}`}
                            >
                              {SECTION_LABELS[role]}
                            </span>
                          ))
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {/* Prázdná buňka by u karty bez destinací vypadala jako chyba načtení;
                          karta bez destinací je legitimní stav (F2.2). Na webu se ukázat
                          může (`/o-nas`), jen ne na stránkách destinací. */}
                      {team.destinationNames.length === 0 ? (
                        <span className="text-sm italic text-gray-400">
                          {t("people.list.noDestinations")}
                        </span>
                      ) : (
                        <span className="flex flex-wrap items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                          <MapPin className="h-3.5 w-3.5" />
                          {team.destinationNames.map((name) => (
                            <span
                              key={name}
                              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700"
                            >
                              {name}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{team.sortOrder}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/destination-teams/${team.id}`} className="btn-ghost btn-sm">
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          title={t("common.actions.delete")}
                          onClick={() => handleDelete(team.id, team.name)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
